import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import {
  Article,
  BuildCircle,
  Close,
  CollapseContent,
  ExpandContent,
  SettingsApplications,
} from '@tempo-io/sui-icons';
import { Separator } from '@tempo-io/sui-separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@tempo-io/sui-sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@tempo-io/sui-tabs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPublishedItemDetailsAdapter } from '@/components/modular-item-panel/adapters/item-details/createPublishedItemDetailsAdapter';
import { createPublishedSystemFieldsAdapter } from '@/components/modular-item-panel/adapters/system-fields/createPublishedSystemFieldsAdapter';
import { TabAdapterProvider } from '@/components/modular-item-panel/context/TabAdapterContext';
import { TooltipIconButton } from '@/components/ui/tooltipIconButton';
import { PublishedCustomFields } from './PublishedCustomFields';
import { PublishedItemDetails } from './PublishedItemDetails';
import { PublishedSystemFields } from './PublishedSystemFields';
import type { IdentifiableTItem, ItemDetails } from './types';

type TabId = 'view' | 'system' | 'custom-fields';

interface PublishedItemPanelProps {
  itemDetails: ItemDetails | null;
  enrichedItem: IdentifiableTItem | null;
  customFieldDefinitions: CustomFieldDTO[];
  hiddenFieldIds?: readonly string[];
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onClose: () => void;
}

export const PublishedItemPanel = ({
  itemDetails,
  enrichedItem,
  customFieldDefinitions,
  hiddenFieldIds,
  expanded,
  onExpandedChange,
  onClose,
}: PublishedItemPanelProps) => {
  const { t } = useTranslation();
  const [panelTab, setPanelTab] = useState<TabId>('system');

  const itemDetailsAdapter =
    itemDetails && enrichedItem
      ? createPublishedItemDetailsAdapter({ itemDetails, enrichedItem, hiddenFieldIds })
      : null;

  const systemFieldsAdapter = itemDetails
    ? createPublishedSystemFieldsAdapter({ itemDetails, hiddenFieldIds })
    : null;

  const toggleExpanded = () => {
    onExpandedChange(!expanded);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const isOpen = itemDetails !== null && enrichedItem !== null;

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        className={cn('flex flex-col gap-0 [&>button]:hidden', {
          '!max-w-7xl': expanded,
          '!max-w-lg': !expanded,
        })}
      >
        <SheetHeader className="border-b-.5 border-border-neutral px-4 pb-2">
          <SheetTitle className="sr-only" />
          <div className="flex items-center justify-end gap-1">
            <TooltipIconButton
              className="shrink-0 p-1 cursor-pointer rounded-xs hover:bg-bg-neutral-hovered"
              icon={expanded ? <CollapseContent /> : <ExpandContent />}
              tooltipText={
                expanded
                  ? t('adaptivePlanner.itemPanel.collapsePanel')
                  : t('adaptivePlanner.itemPanel.expandPanel')
              }
              onClick={toggleExpanded}
            />
            <SheetClose asChild>
              <TooltipIconButton
                className="shrink-0 p-1 cursor-pointer rounded-xs opacity-70 hover:opacity-100 hover:bg-bg-neutral-hovered"
                icon={<Close className="size-4" />}
                tooltipText={t('common.close')}
              />
            </SheetClose>
          </div>
        </SheetHeader>
        <SheetDescription className="sr-only" />
        <Separator />
        <div className="flex h-full flex-row overflow-hidden">
          {expanded && itemDetailsAdapter && (
            <div className="flex-grow border-r-1 border-border-neutral p-4 overflow-y-auto">
              <TabAdapterProvider adapter={itemDetailsAdapter}>
                <PublishedItemDetails />
              </TabAdapterProvider>
            </div>
          )}
          <Tabs
            value={panelTab}
            className="flex h-full w-full max-w-lg flex-row gap-0 overflow-hidden"
          >
            <div className="flex-1 border-r-1 border-border-neutral bg-bg-neutral-subtle p-4 overflow-y-auto">
              <TabsContent value="view">
                {itemDetailsAdapter && (
                  <TabAdapterProvider adapter={itemDetailsAdapter}>
                    <PublishedItemDetails />
                  </TabAdapterProvider>
                )}
              </TabsContent>
              <TabsContent value="system">
                {systemFieldsAdapter && (
                  <TabAdapterProvider adapter={systemFieldsAdapter}>
                    <PublishedSystemFields />
                  </TabAdapterProvider>
                )}
              </TabsContent>
              <TabsContent value="custom-fields">
                {itemDetails && (
                  <PublishedCustomFields
                    itemDetails={itemDetails}
                    customFieldDefinitions={customFieldDefinitions}
                  />
                )}
              </TabsContent>
            </div>

            <TabsList className="flex min-h-[7rem] h-max flex-col bg-bg-surface-base">
              <TabsTrigger
                className={cn('cursor-pointer', panelTab === 'view' && 'bg-bg-neutral')}
                value="view"
                onClick={() => setPanelTab('view')}
                asChild
              >
                <TooltipIconButton
                  icon={<Article />}
                  tooltipText={t('adaptivePlanner.itemPanel.itemSection.itemDetails')}
                />
              </TabsTrigger>
              <TabsTrigger
                className={cn('cursor-pointer', panelTab === 'system' && 'bg-bg-neutral')}
                value="system"
                onClick={() => setPanelTab('system')}
                asChild
              >
                <TooltipIconButton
                  icon={<SettingsApplications />}
                  tooltipText={t('adaptivePlanner.itemPanel.systemFields')}
                />
              </TabsTrigger>
              <TabsTrigger
                className={cn('cursor-pointer', panelTab === 'custom-fields' && 'bg-bg-neutral')}
                value="custom-fields"
                onClick={() => setPanelTab('custom-fields')}
                asChild
              >
                <TooltipIconButton
                  icon={<BuildCircle />}
                  tooltipText={t('adaptivePlanner.itemPanel.customFields')}
                  aria-label={t('adaptivePlanner.itemPanel.customFields')}
                />
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <SheetFooter className="border-t-1 border-border-neutral items-end">
          <SheetClose asChild>
            <Button type="submit">{t('common.close')}</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
